import axios from 'axios'

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AxiosInstance, END_POINTS } from '../../integrations'
import { COIN_UTIL } from '../../util/mapCoinName'

export const fetchConfig = createAsyncThunk('get/getConfig', async () => {
    try {
        const response = await AxiosInstance.get(`${END_POINTS.configApi} `)
        return response
    } catch (error: any) {
        return error.message
    }
})

export const fetchLeaderBoard = createAsyncThunk(
    'get/getRank',
    async (data: {
        limit: number
        page: number | number[]
        boardName: string
        stats?: boolean
    }) => {
        try {
            const response = await AxiosInstance.get(
                `${END_POINTS.rankingApi}${data.boardName}?limit=${data.limit}&page=${data.page}&stats=${data.stats}`
            )
            return response
        } catch (error: any) {
            return error.message
        }
    }
)
export const fetchMyRankingLeaderBoard = createAsyncThunk(
    'get/getMyRanking',
    async (data: {
        limit: number
        page: number
        boardName: string
        stats?: boolean
        userRank?: number
    }) => {
        let result: any = []
        const { page } = data
        let pageToCallApi: number[] = []
        if (page === 1) {
            pageToCallApi = [1, 2]
        } else {
            pageToCallApi = [page - 1, page, page + 1]
        }

        try {
            for (let i = 0; i < pageToCallApi.length; i++) {
                const response = await AxiosInstance.get(
                    `${END_POINTS.rankingApi}${data.boardName}?limit=${data.limit}&page=${pageToCallApi[i]}&stats=${data.stats}`
                )
                result.push(response)
            }
            result = result.flat(1)
            const index = result.findIndex(
                (x: { rank: number }) => x.rank === data.userRank
            )
            const startIndex = index > 50 ? index - 50 : 0
            const endIndex = index < 50 ? index + (100 - index) : index + 51
            const arraySlice = (result = result.slice(startIndex, endIndex))
            return arraySlice
        } catch (error: any) {
            return error.message
        }
    }
)

export const fetchSeason = createAsyncThunk('get/getSeason', async () => {
    let seasonKeyword = window.localStorage.getItem('SEASON_KEYWORD_CONFIG')
    if (!seasonKeyword) {
        seasonKeyword = 'free2'
    }
    try {
        const response: SeasonRequest = await AxiosInstance.get(
            `${END_POINTS.seasonAPI}${seasonKeyword}`
        )
        return response.Season
    } catch (error: any) {
        return error.message
    }
})

export const fetchNftDetail = createAsyncThunk(
    'get/getNftDetail',
    async (nftId: string) => {
        try {
            const response: NftDetailRequest = await AxiosInstance.get(
                `${END_POINTS.nftDetailApi}${nftId}`
            )

            return response.nft
        } catch (error: any) {
            return error.message
        }
    }
)

export const getProducts = createAsyncThunk('get/getProducts', async () => {
    try {
        const response = await axios.get(END_POINTS.productsAPI)
        return response.data
    } catch (error: any) {
        console.log(error, 'error')
    }
})

export const searchUserTag = createAsyncThunk(
    'get/searchUser',
    async (userTag: string) => {
        const value = encodeURIComponent(userTag)
        try {
            const response = await AxiosInstance.get(
                `${END_POINTS.searchApi}?user_tag=${value}`
            )
            return response
        } catch (error: any) {
            return error.message
        }
    }
)

const initialState: leaderBoardState = {
    rank: [],
    status: 'idle',
    error: '',
    searchResults: [],
}

export const leaderBoardSlice = createSlice({
    name: 'leaderBoard',
    initialState,
    reducers: {
        setRewards: (
            state,
            action: PayloadAction<{
                products: Product[]
                season: Season
            }>
        ) => {
            state.rewards = COIN_UTIL.mapSeasonRewards(
                action.payload.products,
                action.payload.season
            )
        },
    },
    extraReducers(buidler) {
        buidler
            .addCase(fetchConfig.pending, (state) => {
                state.status = 'pending'
            })
            .addCase(fetchConfig.fulfilled, (state, action) => {
                state.status = 'success'
                const payload = action.payload as Config
                const seasonKey =
                    payload?.configuration?.active_seasons?.filter(
                        (item: ActiveSeason) =>
                            item.status === 'running' ||
                            item.status === 'pre-heating' ||
                            item.status === 'finished' ||
                            item.status === 'calculating-prizes'
                    )[0]?.key

                seasonKey &&
                    window.localStorage.setItem(
                        'SEASON_KEYWORD_CONFIG',
                        seasonKey
                    )
                state.config = action.payload
            })
            .addCase(fetchConfig.rejected, (state, action) => {
                state.status = 'rejected'
                state.error = action.error.message
            })

            .addCase(fetchLeaderBoard.fulfilled, (state, action) => {
                state.status = 'success'
                state.rank = action.payload?.ranks
                    ? action.payload.ranks
                    : action.payload
                state.statistic = action.payload?.statistic
                    ? action.payload.statistic
                    : null
            })
            .addCase(fetchLeaderBoard.rejected, (state, action) => {
                state.status = 'rejected'
                state.error = action.error.message
            })
            .addCase(fetchSeason.pending, (state, action) => {
                state.status = 'pending'
            })
            .addCase(fetchSeason.fulfilled, (state, action) => {
                state.status = 'success'
                state.season = action.payload
            })
            .addCase(fetchSeason.rejected, (state, action) => {
                state.status = 'rejected'
                state.error = action.error.message
            })

            .addCase(fetchNftDetail.pending, (state, action) => {
                state.status = 'pending'
            })
            .addCase(fetchNftDetail.fulfilled, (state, action) => {
                state.status = 'success'
                state.nftDetail = action.payload
            })
            .addCase(fetchNftDetail.rejected, (state, action) => {
                state.status = 'rejected'
                state.error = action.error.message
            })

            .addCase(fetchMyRankingLeaderBoard.fulfilled, (state, action) => {
                state.status = 'success'
                state.rank = action.payload
            })
            .addCase(fetchMyRankingLeaderBoard.rejected, (state, action) => {
                state.status = 'rejected'
                state.error = action.error.message
            })
            .addCase(fetchMyRankingLeaderBoard.pending, (state, action) => {
                state.status = 'pending'
            })

            .addCase(getProducts.pending, (state, action) => {
                state.status = 'pending'
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                state.products = action.payload
            })
            .addCase(getProducts.rejected, (state, action) => {
                state.error = action.error.message
            })
            .addCase(searchUserTag.pending, (state, action) => {
                state.status = 'pending'
                state.searchResults = []
            })

            .addCase(searchUserTag.fulfilled, (state, action) => {
                state.status = 'success'
                const searchedData =
                    action.payload === undefined ? [] : [action.payload]
                state.searchResults = searchedData
            })
    },
})
export const { setRewards } = leaderBoardSlice.actions
export default leaderBoardSlice.reducer
